<script setup lang="ts">
import { onBeforeMount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import TtButton from '@/components/TtButton.vue'
import { store } from '@/storehandler/tokens'
import { useAccountStore } from '@/store/account'
import { useLocationStore } from '@/store/location'
import { useNotificationsStore } from '@/store/notifications'
import { format, isAfter } from 'date-fns'
import sv from 'date-fns/locale/sv'

const { t } = useI18n({ useScope: 'global' })
const baseURL = import.meta.env.VITE_GATEWAY_URL
const { user } = useAccountStore()
const { deleteParkingBooking } = useLocationStore()
const notificationsStore = useNotificationsStore()
const today = new Date()
const bookingHeadings = ref<String[]>([
  t('tools.id'),
  t('tools.fromDate'),
  t('tools.toDate'),
  t('tools.parking'),
  t('tools.location'),
  t('tools.regNo'),
  t('tools.price'),
  ''
])
const isFetching = ref(false)
const bookings = ref([])

onBeforeMount(() => {
  isFetching.value = true
})

const fetchBookings = async () => {
  isFetching.value = true
  const response = await fetch(
    baseURL + '/user/' + user?.id + '/location-bookings',
    {
      headers: {
        'tt-token': store.get('tt-token')?.token
      }
    }
  ).then((res) => res.json())
  if (!response?.bookings || response.bookings.length === 0) {
    isFetching.value = false
    return
  }
  bookings.value = response.bookings.map((booking: any) => {
    return {
      id: booking.id,
      parking: booking.parking,
      location: booking.location,
      to: booking.to,
      from: booking.from,
      reg_number: booking.reg_number
    }
  })

  isFetching.value = false
}

onMounted(async () => {
  fetchBookings()
})

const cancelBooking = async (id: string) => {
  if (id) {
    const res = await deleteParkingBooking(id)
    if (res?.success) {
      const timeouts = new Map()
      bookings.value = bookings.value.filter((booking: any) => booking.id !== id)
      notificationsStore.addNotification(
        'booking-cancelled',
        t('location.bookingCancelled'),
        'notification',
        false,
        false,
        false,
        false
      )
      clearTimeout(timeouts.get('booking-cancelled'))
      timeouts.set(
        'booking-cancelled',
        setTimeout(() => {
          notificationsStore.removeNotification('booking-cancelled')
        }, 2000)
      )
    }
  }
}
</script>

<template>
  <div>
    <div className="flex justify-between items-center w-full pr-6">
      <h2 class="text-2xl font-semibold">{{ t('tools.bookings') }}</h2>
    </div>
    <table
      class="w-full table-auto border-separate border-spacing-y-3 text-tt-gray"
    >
      <thead>
        <tr>
          <th
            v-for="item in bookingHeadings"
            class="p-2 text-left text-base font-medium first:px-4"
          >
            {{ item }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(booking, i) in bookings.slice().reverse()"
          :key="booking.id"
        >
          <td class="rounded-s-lg bg-white px-4 py-2">{{ booking.id?.substring(24) }}</td>
          <td class="bg-white p-2">{{ format(new Date(booking.from), 'yyyy-MM-dd kk:mm', { locale: sv }) }}</td>
          <td class="bg-white p-2">{{ format(new Date(booking.to), 'yyyy-MM-dd kk:mm', { locale: sv }) }}</td>
          <td class="bg-white p-2">{{ booking.parking.name }}</td>
          <td class="bg-white p-2">{{ booking.location.name }}</td>
          <td class="bg-white p-2">{{ booking.reg_number }}</td>
          <td class="bg-white p-2">{{ booking.parking.price }} SEK</td>
          <td class="bg-white p-2 text-right">
            <TtButton
              :as="'button'"
              :size="'xsmall'"
              :type="'tertiary'"
              @click="cancelBooking(booking.id)"
              :disabled="isAfter(today, new Date(booking.from))"
            >
              {{ t('tools.cancel') }}
            </TtButton>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
